// utils/setupCharCounters.ts

import { countCharsRemaining } from './charsRemaining';

/**
 * Sets up a character counter for a given input element.
 * Updates the remaining character count in the target element and listens for input changes.
 * Optionally listens to a modifier element (e.g., a <select>) to adjust counting behavior.
 *
 * Expects the input element to have the following data attributes:
 * - `data-char-counter-target`: ID of the element containing the `.char-counter__remaining` span.
 * - `data-char-modifier-target` (optional): ID of a modifier element (e.g., <select>).
 * - `data-max-chars`: Maximum number of characters allowed.
 *
 * @param {HTMLInputElement} el - The input element to attach the character counter to.
 */
export function setupCharCounters(el: HTMLInputElement): void {
	const counterId = el.dataset.charCounterTarget;
	const modifierId = el.dataset.charModifierTarget;

	const maxChars = parseInt(el.dataset.maxChars || '', 10);
	if (isNaN(maxChars)) return;

	const charsRemainingElem = document.querySelector<HTMLElement>(`#${counterId}`);
	if (!charsRemainingElem) return;

	const counterElem = charsRemainingElem.querySelector<HTMLElement>('.char-counter__remaining');
	if (!counterElem) return;

	const modifierElem = modifierId
		? document.querySelector<HTMLInputElement>(`#${modifierId}`)
		: null;

	// Initiate on document ready
	countCharsRemaining(
		el as HTMLInputElement,
		charsRemainingElem,
		counterElem,
		modifierElem,
		maxChars,
	);

	// Fire on main listener input
	el.addEventListener('input', () => {
		countCharsRemaining(
			el as HTMLInputElement,
			charsRemainingElem,
			counterElem,
			modifierElem,
			maxChars,
		);
	});

	if (modifierElem instanceof HTMLSelectElement) {
		// Fire when modifier element has changed
		// Adapt this in future to handle text/other inputs if required
		modifierElem.addEventListener('change', () => {
			countCharsRemaining(
				el as HTMLInputElement,
				charsRemainingElem,
				counterElem,
				modifierElem,
				maxChars,
			);
		});
	}
}
